import moment from 'moment';
import { createSlice } from '@reduxjs/toolkit';
import { RAILCARD_CODES } from '../../constants';

const initialState = {
  isLoading: false,
  error: null,
  discounts: null,
  railcards: [],
  filterOptions: {
    age: null,
    travelWith: null,
    travelSE: null,
  },
  shownRailcards: [],
  noResultRailcard: null,
  railcardsTimestamp: null,
};

const isPriceDiscounted = (discountedPrice, originalPrice) =>
  discountedPrice > 0 && discountedPrice < originalPrice;

export const applyDiscounts = (railcards, discounts) => {
  return railcards.map((railcard) => {
    const railcardDiscount = discounts?.railcards?.[railcard.railcardCode];

    if (!discounts?.discountId || !railcardDiscount) {
      return railcard;
    }

    // The discount endpoint echoes back every plan's price, reduced or not, so a plan counts as
    // discounted only when its price undercuts the original one (CORE-22807).
    return {
      ...railcard,
      ...(isPriceDiscounted(railcardDiscount.oneYearPrice, railcard.oneYearPrice) && {
        oneYearPriceDiscounted: railcardDiscount.oneYearPrice,
      }),
      ...(isPriceDiscounted(railcardDiscount.threeYearPrice, railcard.threeYearPrice) && {
        threeYearPriceDiscounted: railcardDiscount.threeYearPrice,
      }),
      discountId: discounts.discountId,
    };
  });
};

const filterRailcardsHelper = (railcards, filters, discounts) => {
  let filteredResult = railcards;

  for (const filterName in filters) {
    const filterValue = filters[filterName];

    if (!filterValue) {
      continue;
    }

    switch (filterName) {
      case 'age':
        filteredResult = filteredResult.filter(({ minAge, maxAge }) => {
          const [filterMin, filterMax] = filterValue.split('-');

          const isMinMet = minAge ? filterMin >= minAge : true;
          const isMaxMet = maxAge ? filterMax <= maxAge : true;

          return isMinMet && isMaxMet;
        });
        break;
      case 'travelWith':
        filteredResult = filteredResult.filter(({ railcardCode }) => {
          if (filterValue === 'children') {
            return railcardCode !== RAILCARD_CODES.TWO_TOGETHER;
          }

          if (filterValue === 'partner') {
            return railcardCode !== RAILCARD_CODES.FAMILY_AND_FRIENDS;
          }

          if (filterValue === 'alone') {
            return ![RAILCARD_CODES.TWO_TOGETHER, RAILCARD_CODES.FAMILY_AND_FRIENDS].includes(
              railcardCode,
            );
          }

          return true;
        });
        break;
      case 'travelSE':
        filteredResult = filteredResult.filter(({ railcardCode }) => {
          if (filterValue === 'no') {
            return railcardCode !== RAILCARD_CODES.NETWORK;
          }
          return true;
        });
        break;
      default:
        return true;
    }
  }

  return applyDiscounts(filteredResult, discounts);
};

const getDefaultRailcard = (railcards) => {
  return railcards.find(({ railcardCode }) => railcardCode === RAILCARD_CODES.TWO_TOGETHER);
};

export const railcardsSlice = createSlice({
  name: 'railcards',
  initialState,
  reducers: {
    cleanupDiscountedRailcards: (state) => {
      // Remove discount-related properties from railcards and shownRailcards
      const clean = (arr) =>
        arr.map((railcard) => {
          delete railcard.oneYearPriceDiscounted;
          delete railcard.threeYearPriceDiscounted;
          delete railcard.discountId;
          return railcard;
        });
      state.railcards = clean(state.railcards);
      state.shownRailcards = clean(state.shownRailcards);
    },
    getRailcards: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    getRailcardsSuccess: (state, action) => {
      state.isLoading = false;
      state.railcards = action.payload;
      state.shownRailcards = filterRailcardsHelper(
        state.railcards,
        state.filterOptions,
        state.discounts,
      );
      state.noResultRailcard = getDefaultRailcard(action.payload);
      state.railcardsTimestamp = moment().format('YYYY-MM-DD');
    },
    getRailcardsFailure: (state, action) => {
      state.error = action.payload.message;
      state.isLoading = false;
      state.railcards = [];
      state.shownRailcards = [];
    },
    filterRailcards: (state, action) => {
      const { data } = action;
      state.filterOptions[data.name] = data.value;
      state.shownRailcards = filterRailcardsHelper(
        state.railcards,
        state.filterOptions,
        state.discounts,
      );
    },
    resetFilters: (state) => {
      state.filterOptions = initialState.filterOptions;
      state.shownRailcards = filterRailcardsHelper(
        state.railcards,
        state.filterOptions,
        state.discounts,
      );
    },
    setDiscounts: (state, action) => {
      state.discounts = action.payload.discounts;
      state.shownRailcards = filterRailcardsHelper(
        state.railcards,
        state.filterOptions,
        state.discounts,
      );
    },
  },
});

export const {
  getRailcards,
  getRailcardsSuccess,
  getRailcardsFailure,
  resetFilters,
  cleanupDiscountedRailcards,
} = railcardsSlice.actions;

export const selectRailcards = (state) => state.railcards;

export default railcardsSlice.reducer;
